import { createBlankDocument, type CurvaDocument } from "@/document/model/document";
import { createEmptyScriptLibrary } from "@/document/model/documentScripts";
import { fetchBundledDefaultEditorScriptsBytes } from "@/io/defaultEditorScripts";
import {
  copyBytesToArrayBuffer,
  EDITOR_SCRIPTS_RECORD_KEY,
  EDITOR_SCRIPTS_STORE,
  idbRequestToPromise,
  idbTransactionDone,
  isIndexedDbAvailable,
  isSafeMode,
  openCurvaEditorDatabase,
  type PersistedEditorScriptsRecord,
} from "@/io/documentPersistence";
import { deserializeCurvaPackage, serializeCurvaPackage } from "@/io/package";
import { isNativeShell } from "@/io/platform";
import {
  tauriClearEditorScriptsFile,
  tauriLoadEditorScriptsBytes,
  tauriSaveEditorScriptsBytes,
  tauriSeedDefaultEditorScriptsFromBundled,
} from "@/io/platform/tauri";

export const EDITOR_SCRIPTS_DOCUMENT_NAME = "Editor Scripts";
/** Sentinel path for workspace sessions editing the editor-scripts package (web). */
export const EDITOR_SCRIPTS_SESSION_PATH = "curva:editor-scripts";

export function createEmptyEditorScriptsDocument(): CurvaDocument {
  const document = createBlankDocument(EDITOR_SCRIPTS_DOCUMENT_NAME);
  document.scripts = createEmptyScriptLibrary();
  return document;
}

async function loadPersistedEditorScriptsFromIdb(): Promise<CurvaDocument | undefined> {
  if (!isIndexedDbAvailable()) {
    return undefined;
  }

  const database = await openCurvaEditorDatabase();
  try {
    const transaction = database.transaction(EDITOR_SCRIPTS_STORE, "readonly");
    const record = await idbRequestToPromise<PersistedEditorScriptsRecord | undefined>(
      transaction.objectStore(EDITOR_SCRIPTS_STORE).get(EDITOR_SCRIPTS_RECORD_KEY),
    );
    if (!record) {
      return undefined;
    }
    return deserializeCurvaPackage(new Uint8Array(record.bytes)).document;
  } finally {
    database.close();
  }
}

async function seedFromBundledDefaults(): Promise<CurvaDocument | undefined> {
  const bytes = isNativeShell()
    ? await tauriSeedDefaultEditorScriptsFromBundled()
    : await fetchBundledDefaultEditorScriptsBytes();
  if (!bytes || bytes.byteLength === 0) {
    return undefined;
  }

  const document = deserializeCurvaPackage(bytes).document;
  document.name = document.name || EDITOR_SCRIPTS_DOCUMENT_NAME;

  // Persist so subsequent boots and Clear→reload behave consistently.
  if (!isNativeShell()) {
    // Native seed already wrote AppConfig; web still needs IndexedDB.
    try {
      await saveEditorScriptsDocument(document);
    } catch (error) {
      console.warn("[curva] failed to persist seeded editor scripts", error);
    }
  }

  return document;
}

export async function loadEditorScriptsDocument(): Promise<CurvaDocument | undefined> {
  if (isNativeShell()) {
    const bytes = await tauriLoadEditorScriptsBytes();
    if (bytes && bytes.byteLength > 0) {
      return deserializeCurvaPackage(bytes).document;
    }
    return seedFromBundledDefaults();
  }

  const existing = await loadPersistedEditorScriptsFromIdb();
  if (existing) {
    return existing;
  }
  return seedFromBundledDefaults();
}

export async function saveEditorScriptsDocument(document: CurvaDocument): Promise<void> {
  if (isSafeMode()) {
    console.info("[curva] safe mode: editor scripts persistence writes are disabled");
    return;
  }

  const named = {
    ...document,
    name: document.name || EDITOR_SCRIPTS_DOCUMENT_NAME,
  };
  const bytes = serializeCurvaPackage(named);

  if (isNativeShell()) {
    await tauriSaveEditorScriptsBytes(bytes);
    return;
  }

  if (!isIndexedDbAvailable()) {
    return;
  }

  const record: PersistedEditorScriptsRecord = {
    id: EDITOR_SCRIPTS_RECORD_KEY,
    bytes: copyBytesToArrayBuffer(bytes),
    savedAt: new Date().toISOString(),
  };

  const database = await openCurvaEditorDatabase();
  try {
    const transaction = database.transaction(EDITOR_SCRIPTS_STORE, "readwrite");
    const store = transaction.objectStore(EDITOR_SCRIPTS_STORE);
    await Promise.all([
      idbRequestToPromise(store.put(record)),
      idbTransactionDone(transaction),
    ]);
  } finally {
    database.close();
  }
}

export async function clearEditorScriptsDocument(): Promise<void> {
  if (isSafeMode()) {
    console.info("[curva] safe mode: editor scripts persistence writes are disabled");
    return;
  }

  if (isNativeShell()) {
    await tauriClearEditorScriptsFile();
    return;
  }

  if (!isIndexedDbAvailable()) {
    return;
  }

  const database = await openCurvaEditorDatabase();
  try {
    const transaction = database.transaction(EDITOR_SCRIPTS_STORE, "readwrite");
    const store = transaction.objectStore(EDITOR_SCRIPTS_STORE);
    await Promise.all([
      idbRequestToPromise(store.delete(EDITOR_SCRIPTS_RECORD_KEY)),
      idbTransactionDone(transaction),
    ]);
  } finally {
    database.close();
  }
}

/** Absolute AppConfig path on Tauri; sentinel path on web. */
export async function editorScriptsSessionPath(): Promise<string> {
  if (isNativeShell()) {
    const { appConfigDir, join } = await import("@tauri-apps/api/path");
    return join(await appConfigDir(), "editor-scripts.curva");
  }
  return EDITOR_SCRIPTS_SESSION_PATH;
}

export function isEditorScriptsSessionPath(path: string | undefined): boolean {
  if (!path) {
    return false;
  }
  if (path === EDITOR_SCRIPTS_SESSION_PATH) {
    return true;
  }
  return path.endsWith("/editor-scripts.curva") || path.endsWith("\\editor-scripts.curva");
}
